import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useRaffleCurrentQuery } from "Api/Queries/Raffle/useRaffleCurrentQuery";
import { useRaffleHistoryQuery } from "Api/Queries/Raffle/useRaffleHistoryQuery";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { RaffleMoveTicketsDialog } from "Components/Raffle/RaffleMoveTicketsDialog";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { useCheckPermission } from "Hooks/usePermission";
import {
  Permission,
  type RaffleCompetitionDto,
  type RaffleHistoryItemDto,
} from "Infrastructure/Api/Api";
import { AccessRightCodes } from "Models/AccessRightCodes";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";

const RESOURCES = Resources.Raffle.Page;

/** Closing flips only isOpen, so the source competition stays visible during the fade. */
type MoveDialogState = {
  isOpen: boolean;
  from: RaffleCompetitionDto | null;
};

const getHistoryDateText = (item: RaffleHistoryItemDto): string | null => {
  if (item.drawDate) {
    return formatDate(item.drawDate);
  }
  if (item.closedAt) {
    return formatDate(item.closedAt);
  }
  return null;
};

/**
 * The holder's running competitions, ticket moves and history (spec §8, R7). Ticket numbers are
 * deliberately absent: only the operator maps numbers to people, after the draw.
 */
export const RafflePage = () => {
  const { t } = useResource();
  const [moveDialog, setMoveDialog] = useState<MoveDialogState>({
    isOpen: false,
    from: null,
  });
  const {
    data: current,
    isPending: isCurrentPending,
    isError: isCurrentError,
  } = useRaffleCurrentQuery();
  const {
    data: history,
    isPending: isHistoryPending,
    isError: isHistoryError,
  } = useRaffleHistoryQuery();

  // Moving needs Raffle Update: a Read-only role must not be offered a certain 403.
  const hasMoveRight = useCheckPermission(
    AccessRightCodes.Raffle,
    Permission.Update,
  );

  const competitions = current?.competitions ?? [];
  const canMove = hasMoveRight && competitions.length > 1;

  const competitionTitle = (competition: RaffleCompetitionDto) =>
    competition.name ?? (competition.isDefault ? t(RESOURCES.MainDraw) : null);

  return (
    <AuthenticatedLayout title={t(RESOURCES.Title)}>
      <StyledPageWrapper>
        <Stack gap={3}>
          {isCurrentError && <ErrorText>{t(RESOURCES.LoadError)}</ErrorText>}
          {isCurrentPending && (
            <Stack alignItems="center" py={4}>
              <CircularProgress />
            </Stack>
          )}

          {!isCurrentPending && !isCurrentError && (
            <>
              {current?.isRunning !== true && (
                <Typography color="text.secondary">
                  {t(RESOURCES.NotRunning)}
                </Typography>
              )}

              {competitions.map((competition) => (
                <Card key={competition.roundPublicID}>
                  <CardContent>
                    <Stack gap={1}>
                      {competitionTitle(competition) && (
                        <Typography variant="h6">
                          {competitionTitle(competition)}
                        </Typography>
                      )}
                      <Typography>
                        {t(RESOURCES.MyTickets)}:{" "}
                        <b>{competition.myTicketCount}</b>
                      </Typography>
                      <Typography>
                        {t(RESOURCES.DrawDate)}:{" "}
                        <b>
                          {competition.isAnnounced && competition.drawDate
                            ? formatDate(competition.drawDate)
                            : t(Resources.Raffle.Dashboard.DateToBeAnnounced)}
                        </b>
                      </Typography>
                      {competition.rewards.length > 0 && (
                        <>
                          <Typography variant="subtitle2" mt={1}>
                            {t(RESOURCES.Rewards)}
                          </Typography>
                          <List dense disablePadding>
                            {competition.rewards.map((reward) => (
                              <ListItem key={reward.order} disableGutters>
                                <ListItemText
                                  primary={
                                    reward.quantity && reward.quantity > 1
                                      ? `${reward.quantity}× ${reward.title}`
                                      : reward.title
                                  }
                                  secondary={reward.description}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </>
                      )}
                      {canMove && competition.myTicketCount > 0 && (
                        <Stack direction="row">
                          <Button
                            size="small"
                            onClick={() =>
                              setMoveDialog({ isOpen: true, from: competition })
                            }
                          >
                            {t(RESOURCES.MoveTickets)}
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </>
          )}

          <Stack gap={1}>
            <Typography variant="h6">{t(RESOURCES.History)}</Typography>
            {isHistoryError && <ErrorText>{t(RESOURCES.LoadError)}</ErrorText>}
            {isHistoryPending && (
              <Stack alignItems="center" py={2}>
                <CircularProgress />
              </Stack>
            )}
            {history?.length === 0 && (
              <Typography color="text.secondary">
                {t(RESOURCES.HistoryEmpty)}
              </Typography>
            )}
            {history?.map((item) => {
              const dateText = getHistoryDateText(item);
              const ticketsText = t(RESOURCES.HistoryTickets, {
                count: item.myTicketCount,
              });

              return (
                <Card key={item.roundPublicID}>
                  <CardContent>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={2}
                    >
                      <Stack>
                        <Typography variant="subtitle1">
                          {item.roundName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {dateText
                            ? `${dateText} · ${ticketsText}`
                            : ticketsText}
                        </Typography>
                      </Stack>
                      {item.isDrawn && (
                        <Chip
                          size="small"
                          color="success"
                          label={t(RESOURCES.Drawn)}
                        />
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Stack>
      </StyledPageWrapper>

      <RaffleMoveTicketsDialog
        isOpen={moveDialog.isOpen}
        from={moveDialog.from}
        competitions={competitions}
        onClose={() => setMoveDialog((state) => ({ ...state, isOpen: false }))}
      />
    </AuthenticatedLayout>
  );
};
